import moment from "moment";

const overlapsMonth = (rangeStart, rangeEnd, periodStart, periodEnd) => {
	if (rangeEnd && rangeEnd.isBefore(periodStart, "day")) return false;
	if (rangeStart && rangeStart.isAfter(periodEnd, "day")) return false;
	return true;
};

const getMonthRange = (year, month) => {
	const periodStart = moment([year, month - 1, 1]).startOf("day");
	return {
		periodStart,
		periodEnd: moment(periodStart).endOf("month"),
	};
};

const toDayMoment = (value) =>
	value ? moment(value).startOf("day") : null;

/**
 * Rangos de contrato conocidos del empleado.
 * El contrato en curso y el historial se suman: un día vale si lo cubre
 * cualquiera de ellos. Las fechas de alta solo se usan cuando no hay
 * ninguna asignación de contrato.
 */
const normalizeContractRanges = (employee) => {
	const ranges = [];

	if (employee.employee_with_contract) {
		ranges.push({
			start_day: employee.employee_with_contract.start_day,
			end_day: employee.employee_with_contract.end_day,
		});
	}

	if (Array.isArray(employee.employee_contracts)) {
		employee.employee_contracts.forEach((contractAssignment) => {
			ranges.push({
				start_day: contractAssignment.start_day,
				end_day: contractAssignment.end_day,
			});
		});
	}

	if (ranges.length > 0) return ranges;

	if (employee.start_day || employee.end_day) {
		return [{ start_day: employee.start_day, end_day: employee.end_day }];
	}

	return [];
};

const hasActiveContractInMonth = (employee, periodStart, periodEnd) => {
	const ranges = normalizeContractRanges(employee);
	if (ranges.length === 0) return false;

	return ranges.some((range) =>
		overlapsMonth(
			toDayMoment(range.start_day),
			toDayMoment(range.end_day),
			periodStart,
			periodEnd
		)
	);
};

const normalizeEmployeeGroups = (employee, groupId) => {
	if (Array.isArray(employee.groups) && employee.groups.length > 0) {
		return employee.groups;
	}

	if (employee.employee_with_group) {
		return [
			{
				id: groupId
					? parseInt(groupId, 10)
					: employee.employee_with_group.group_id,
				start_day: employee.employee_with_group.start_day,
				end_day: employee.employee_with_group.end_day,
			},
		];
	}

	if (
		Array.isArray(employee.employee_groups) &&
		employee.employee_groups.length > 0
	) {
		return employee.employee_groups.map((employeeGroup) => ({
			id: employeeGroup.group?.id ?? employeeGroup.group_id,
			start_day: employeeGroup.start_day,
			end_day: employeeGroup.end_day,
		}));
	}

	return undefined;
};

const hasActiveGroupInMonth = (employee, groupId, periodStart, periodEnd) => {
	const groups = normalizeEmployeeGroups(employee, groupId);
	if (!groups || groups.length === 0) {
		return false;
	}

	const parsedGroupId = groupId ? parseInt(groupId, 10) : null;

	return groups.some((groupItem) => {
		if (parsedGroupId && groupItem.id !== parsedGroupId) return false;

		return overlapsMonth(
			toDayMoment(groupItem.start_day),
			toDayMoment(groupItem.end_day),
			periodStart,
			periodEnd
		);
	});
};

/**
 * Comprueba si el empleado tiene un contrato vigente en un día concreto.
 */
export function hasActiveContractOnDay(employee, day) {
	if (!employee?.id) return false;
	if (employee.enabled === false) return false;

	const target = moment(day).startOf("day");
	const ranges = normalizeContractRanges(employee);
	if (ranges.length === 0) return false;

	return ranges.some((range) => {
		const start = toDayMoment(range.start_day);
		const end = toDayMoment(range.end_day);
		if (start && target.isBefore(start, "day")) return false;
		if (end && target.isAfter(end, "day")) return false;
		return true;
	});
}

/**
 * Comprueba si el empleado tiene contrato activo en el mes.
 */
export function isEmployeeActiveInMonth(employee, year, month) {
	if (!employee?.id) return false;
	if (employee.enabled === false) return false;

	const { periodStart, periodEnd } = getMonthRange(year, month);
	return hasActiveContractInMonth(employee, periodStart, periodEnd);
}

/**
 * Indica si un empleado debe mostrarse en la planificación de ausencias del equipo.
 * Prioridad: contrato activo en el mes y, después, grupo activo en el mes.
 */
export function isEmployeeVisibleInTeamAbsences(
	employee,
	year,
	month,
	groupId,
	groupScopedList = false
) {
	if (!employee?.id) return false;
	if (employee.enabled === false) return false;

	const { periodStart, periodEnd } = getMonthRange(year, month);

	if (!hasActiveContractInMonth(employee, periodStart, periodEnd)) {
		return false;
	}

	if (groupId) {
		return hasActiveGroupInMonth(employee, groupId, periodStart, periodEnd);
	}

	const groups = normalizeEmployeeGroups(employee, groupId);
	if (groups) {
		return hasActiveGroupInMonth(employee, groupId, periodStart, periodEnd);
	}

	return groupScopedList;
}
